import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Circle, FileText, ArrowLeft, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import MeetingBriefPanel from '../components/BDAssistant/MeetingBriefPanel';
import MeetingCapturePanel from '../components/BDAssistant/MeetingCapturePanel';
import MeetingDebriefPanel from '../components/BDAssistant/MeetingDebriefPanel';
import StartEngagementModal from '../components/BDAssistant/StartEngagementModal';

/**
 * BD-04 — meeting detail page. Same 3-column layout as BDEngagementDetail.tsx
 * (left stage rail / centre current-stage card / right artifacts panel),
 * cloned rather than shared since that file is owned by two other open
 * tickets and is not touched here.
 *
 * ONE DELIBERATE DIVERGENCE from the engagement page: BDEngagementDetail's
 * getStageStatus derives a 4-state status (upcoming/skipped/current/
 * completed) by comparing a stage's position against a single
 * `engagement.stage` pointer. bd_meetings has no such single pointer — each
 * stage's status (done/waiting/skipped) is stored independently in
 * stage_status, exactly as BD-04's ticket describes it. "Which stage is
 * centered in the UI" is pure client-side selection state (`viewStage`),
 * not something derived from status.
 */

interface Stage {
  key: string;
  label: string;
  description: string;
  hasArtifact: boolean;
}

const STAGES: Stage[] = [
  { key: 'prep_brief', label: '1. Prep Brief', description: 'What to know before the meeting', hasArtifact: true },
  { key: 'capture', label: '2. Capture', description: 'Transcript, voice debrief, or notes', hasArtifact: false },
  { key: 'debrief', label: '3. Debrief', description: 'Summary, commitments, needs', hasArtifact: true },
  { key: 'crm_update', label: '4. CRM Update', description: 'Zoho note and proposed deal changes', hasArtifact: true },
  { key: 'follow_up', label: '5. Follow-up', description: 'Draft reply in your voice', hasArtifact: true },
  { key: 'coach', label: '6. Coach (optional)', description: 'How the call went', hasArtifact: true },
  { key: 'track', label: '7. Track', description: 'Activity record and reminders', hasArtifact: false },
];

const ARTIFACT_LABELS: Record<string, string> = {
  prep_brief: 'Brief',
  debrief: 'Debrief',
  crm_update: 'Zoho Note',
  follow_up: 'Email Draft',
  coach: 'Coach Report',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  awaiting_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  sent: 'bg-blue-100 text-blue-700',
};

interface StageEntry {
  status: 'done' | 'waiting' | 'skipped';
  artifact_status: string | null;
}

interface Meeting {
  id: number;
  title: string;
  stage_status: Record<string, StageEntry>;
  created_at: string;
}

const BDMeetingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  // ?stage=debrief opens straight at that stage (the Debriefs tab links here).
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('stage');
  const [viewStage, setViewStage] = useState<string>(STAGES.some((s) => s.key === requested) ? (requested as string) : 'prep_brief');
  const [saving, setSaving] = useState(false);
  const [startingEngagement, setStartingEngagement] = useState(false);

  // `silent` refreshes the meeting WITHOUT swapping the page for "Loading…": the panels on it call this after they
  // change something, and must not be unmounted by their own refresh.
  const load = useCallback(async (silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/api/bd-meetings/${id}`);
      setMeeting(res.data?.meeting || null);
    } catch (err) {
      toast.error('Failed to load meeting');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  const refresh = useCallback(() => load(true), [load]);

  const setStage = async (stageKey: string, status: StageEntry['status'], artifactStatus?: string | null) => {
    if (!id) return;
    setSaving(true);
    try {
      const res = await api.patch(`/api/bd-meetings/${id}/stage`, { stageKey, status, artifactStatus });
      setMeeting(res.data?.meeting || null);
      toast.success('Updated');
    } catch (err) {
      toast.error('Failed to update stage');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading…</div>;
  if (!meeting) return <div className="p-8 text-center text-gray-400">Meeting not found.</div>;

  const entries = meeting.stage_status || {};
  const artifactRows = STAGES
    .filter((s) => s.hasArtifact && entries[s.key]?.artifact_status != null)
    .map((s) => ({ stage: s, artifactStatus: entries[s.key].artifact_status as string }));

  const viewing = STAGES.find((s) => s.key === viewStage) || STAGES[0];

  return (
    <div className="max-w-6xl mx-auto p-6">
      <button
        onClick={() => navigate('/app/business-development?tab=meetings')}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{meeting.title}</h1>
        <button
          type="button"
          onClick={() => setStartingEngagement(true)}
          className="shrink-0 text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
          data-testid="bd-start-engagement"
        >
          Start engagement
        </button>
      </div>
      {startingEngagement && <StartEngagementModal meetingId={meeting.id} onClose={() => setStartingEngagement(false)} />}

      <div className="grid grid-cols-12 gap-6">
        {/* Left: stage rail */}
        <div className="col-span-3">
          <div className="bg-white border border-gray-200 rounded-xl p-5 sticky top-6">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Meeting Stages</h3>
            <div className="space-y-1">
              {STAGES.map((stage, idx) => {
                const status = entries[stage.key]?.status || 'waiting';
                const isViewing = stage.key === viewStage;
                return (
                  <div key={stage.key} className="relative">
                    {idx < STAGES.length - 1 && (
                      <div className={`absolute left-3.5 top-8 w-0.5 h-6 ${status === 'done' ? 'bg-green-400' : status === 'skipped' ? 'bg-orange-300' : 'bg-gray-200'}`} />
                    )}
                    <div
                      onClick={() => setViewStage(stage.key)}
                      className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer ${isViewing ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {isViewing ? (
                          <div className="w-5 h-5 rounded-full border-2 border-blue-500 bg-blue-500 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-white" />
                          </div>
                        ) : status === 'done' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : status === 'skipped' ? (
                          <div className="w-5 h-5 rounded-full border-2 border-orange-500 bg-orange-500 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-white" />
                          </div>
                        ) : (
                          <Circle className="w-5 h-5 text-gray-300" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${isViewing ? 'text-blue-700' : status === 'skipped' ? 'text-orange-700' : status === 'done' ? 'text-gray-700' : 'text-gray-400'}`}>
                          {stage.label}
                        </p>
                        {(isViewing || status === 'skipped') && (
                          <p className={`text-xs mt-0.5 ${!isViewing && status === 'skipped' ? 'text-orange-500' : 'text-blue-500'}`}>
                            {stage.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Centre: current stage card */}
        <div className="col-span-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900">{viewing.label}</h2>
            <p className="text-sm text-gray-500 mt-1 mb-6">{viewing.description}</p>

            <div className="flex items-center gap-2 mb-6">
              <span className="text-xs text-gray-400">Status:</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {entries[viewing.key]?.status || 'waiting'}
              </span>
            </div>

            {viewing.key === 'prep_brief' && meeting && <MeetingBriefPanel meetingId={meeting.id} />}
            {viewing.key === 'capture' && meeting && <MeetingCapturePanel meetingId={meeting.id} onChanged={refresh} />}
            {viewing.key === 'debrief' && meeting && <MeetingDebriefPanel meetingId={meeting.id} onChanged={refresh} />}

            {viewing.hasArtifact && viewing.key !== 'debrief' && (
              <div className="border border-dashed border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-700">Already have this deliverable?</p>
                <p className="text-xs text-gray-500 mt-1 mb-3">Upload the finished file to use it as-is, or mark this stage as having no notes.</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setStage(viewing.key, 'done', 'draft')}
                    disabled={saving}
                    className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Upload className="w-3.5 h-3.5" /> Upload existing
                  </button>
                  <button
                    onClick={() => setStage(viewing.key, 'skipped', null)}
                    disabled={saving}
                    className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <X className="w-3.5 h-3.5" /> Mark no notes
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: artifacts panel */}
        <div className="col-span-3">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Artifacts ({artifactRows.length})
            </h3>
            {artifactRows.length === 0 ? (
              <div className="text-center py-6 text-gray-400">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No artifacts yet</p>
                <p className="text-xs mt-0.5">Generated outputs appear here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {artifactRows.map(({ stage, artifactStatus }) => (
                  <div key={stage.key} className="border border-gray-100 rounded-lg p-3">
                    <span className="text-xs font-medium text-gray-800">{ARTIFACT_LABELS[stage.key] || stage.key}</span>
                    <div className="mt-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${STATUS_COLORS[artifactStatus] || STATUS_COLORS.draft}`}>
                        {artifactStatus.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BDMeetingDetail;
